import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Key, Copy, RefreshCw, Trash2, Check, Shield, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ProxyKeyManager() {
  const [keyData, setKeyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/proxy-keys/status`);
      setKeyData(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const generateKey = async () => {
    try {
      const res = await axios.post(`${API}/proxy-keys/generate`);
      setNewKey(res.data.key);
      toast.success("Proxy key generated! Save it now — it won't be shown again.");
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate key");
    }
  };

  const regenerateKey = async () => {
    try {
      const res = await axios.post(`${API}/proxy-keys/regenerate`);
      setNewKey(res.data.key);
      toast.success("New key generated. Previous key revoked.");
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to regenerate key");
    }
  };

  const revokeKey = async () => {
    try {
      await axios.post(`${API}/proxy-keys/revoke`);
      setNewKey(null);
      toast.success("Key revoked successfully.");
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke key");
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
    toast.success(`${label} copied!`);
  };

  if (loading) return null;

  const baseUrl = "https://api.qnneal.dev/v1";

  return (
    <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="proxy-key-manager" data-automation-id="proxy-key-manager">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Key className="w-5 h-5 text-[var(--qn-primary)]" />
          Your QNNEAL Proxy Key
        </CardTitle>
        <p className="text-sm text-[var(--qn-foreground-muted)]">
          Use this key in any OpenAI-compatible tool to route requests through QNNEAL's optimization engine.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {!keyData?.has_key && !newKey ? (
          <div className="text-center py-6">
            <Shield className="w-10 h-10 text-[var(--qn-foreground-subtle)] mx-auto mb-3" />
            <p className="text-sm text-[var(--qn-foreground-muted)] mb-4">No proxy key generated yet.</p>
            <Button onClick={generateKey} className="btn-primary" data-testid="generate-proxy-key-btn" data-automation-id="generate-proxy-key-btn">
              <Key className="w-4 h-4 mr-2" />Generate Proxy Key
            </Button>
          </div>
        ) : (
          <>
            {/* New key display */}
            <AnimatePresence>
              {newKey && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20 mb-4">
                    <div className="text-sm font-medium text-emerald-400 mb-2">Save this key now — it won't be shown again:</div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm font-mono bg-[var(--qn-background)] px-3 py-2 rounded border border-[var(--qn-border)] break-all" data-testid="new-proxy-key-value" data-automation-id="new-proxy-key-value">{newKey}</code>
                      <Button size="icon" variant="ghost" onClick={() => copy(newKey, "API Key")} className="shrink-0" data-testid="copy-new-key-btn" data-automation-id="copy-new-key-btn">
                        {copied === "API Key" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Key status */}
            {keyData?.has_key && (
              <>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[var(--qn-foreground-muted)]">API Key</span>
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono text-[var(--qn-foreground)]" data-testid="proxy-key-prefix" data-automation-id="proxy-key-prefix">{keyData.prefix}</code>
                      <Badge variant="outline" className="text-emerald-400 border-emerald-400/30 text-xs">Active</Badge>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[var(--qn-foreground-muted)]">Base URL</span>
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono text-[var(--qn-primary)]" data-testid="proxy-base-url" data-automation-id="proxy-base-url">{baseUrl}</code>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copy(baseUrl, "Base URL")} data-testid="copy-base-url-btn" data-automation-id="copy-base-url-btn">
                        {copied === "Base URL" ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </div>
                  {keyData.last_used_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[var(--qn-foreground-muted)]">Last Used</span>
                      <span className="text-sm">{new Date(keyData.last_used_at).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[var(--qn-foreground-muted)]">Usage Today</span>
                    <span className="text-sm font-mono">{keyData.usage_today} / {keyData.limit_today} requests</span>
                  </div>
                  {keyData.total_saved > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[var(--qn-foreground-muted)]">Total Saved</span>
                      <span className="text-sm font-mono text-emerald-400">${keyData.total_saved.toFixed(4)}</span>
                    </div>
                  )}
                </div>

                {/* Quick Setup */}
                <div className="rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)] p-4 mt-3" data-testid="proxy-quick-setup" data-automation-id="proxy-quick-setup">
                  <div className="text-sm font-medium mb-2">Quick Setup</div>
                  <div className="text-xs text-[var(--qn-foreground-muted)] space-y-1 font-mono">
                    <div>Base URL: <span className="text-[var(--qn-primary)]">{baseUrl}</span></div>
                    <div>API Key: <span className="text-[var(--qn-foreground)]">{keyData.prefix}</span></div>
                  </div>
                  <p className="text-xs text-[var(--qn-foreground-subtle)] mt-2">Paste these into any OpenAI-compatible tool. QNNEAL handles the rest.</p>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={regenerateKey} className="text-xs" data-testid="regenerate-key-btn" data-automation-id="regenerate-key-btn">
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Regenerate
                  </Button>
                  <Button size="sm" variant="outline" onClick={revokeKey} className="text-xs text-red-400 hover:text-red-300 border-red-400/30 hover:border-red-400/50" data-testid="revoke-key-btn" data-automation-id="revoke-key-btn">
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />Revoke
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
